import type { DataProvider } from "@refinedev/core";
import { authProvider } from "./authProvider";
import { supabase } from "../lib/supabaseClient";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

function adminHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = {};
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

// Custom data provider for Mercury Hub AI Tools API
// Connects directly to MongoDB via Mercury Hub REST endpoints
export const dataProvider: DataProvider = {
  getList: async function getList({ resource, pagination }) {
    const params = new URLSearchParams();
    const token = localStorage.getItem("access_token");

    if (pagination) {
      const pageSize = pagination.pageSize ?? 50;
      const currentPage = pagination.currentPage ?? 1;
      params.append("limit", String(pageSize));
      params.append("offset", String((currentPage - 1) * pageSize));
    }

    const response = await fetch(`${API_URL}/api/v1/admin/${resource}?${params.toString()}`, {
      headers: adminHeaders(token),
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Unauthorized - refresh token if possible
        const newToken = await refreshAccessToken();
        if (newToken) {
          return getList({ resource, pagination });
        }
      }
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to fetch ${resource}`);
    }

    const data = await response.json();
    return {
      data: data.data || [],
      total: data.total || data.data?.length || 0,
    };
  },

  getOne: async function getOne({ resource, id }) {
    const token = localStorage.getItem("access_token");

    const response = await fetch(`${API_URL}/api/v1/admin/${resource}/${id}`, {
      headers: adminHeaders(token),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return getOne({ resource, id });
        }
      }
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to fetch ${resource}/${id}`);
    }

    const data = await response.json();
    return { data };
  },

  create: async function create({ resource, variables }) {
    const token = localStorage.getItem("access_token");

    const response = await fetch(`${API_URL}/api/v1/admin/${resource}`, {
      method: "POST",
      headers: { ...adminHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return create({ resource, variables });
        }
      }
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  update: async function update({ resource, id, variables }) {
    const token = localStorage.getItem("access_token");

    const response = await fetch(`${API_URL}/api/v1/admin/${resource}/${id}`, {
      method: "PUT",
      headers: { ...adminHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return update({ resource, id, variables });
        }
      }
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to update ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  deleteOne: async function deleteOne({ resource, id }) {
    const token = localStorage.getItem("access_token");

    const response = await fetch(`${API_URL}/api/v1/admin/${resource}/${id}`, {
      method: "DELETE",
      headers: adminHeaders(token),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return deleteOne({ resource, id });
        }
      }
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to delete ${resource}`);
    }

    const data = await response.json();
    return { data };
  },

  getMany: async ({ resource, ids }) => {
    const token = localStorage.getItem("access_token");

    // Fetch all and filter (no dedicated getMany endpoint)
    const response = await fetch(`${API_URL}/api/v1/admin/${resource}?limit=1000`, {
      headers: adminHeaders(token),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${resource}`);
    }

    const result = await response.json();
    const filtered = (result.data || []).filter((item: { id: string }) => ids.includes(item.id));
    return { data: filtered };
  },

  getApiUrl: () => API_URL,

  custom: async function custom({ url, method, payload, headers: customHeaders, query }) {
    let requestUrl = url;

    if (query) {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([key, value]) => {
        params.append(key, String(value));
      });
      requestUrl += `?${params.toString()}`;
    }

    const token = localStorage.getItem("access_token");

    const response = await fetch(requestUrl, {
      method: method || "GET",
      headers: {
        ...adminHeaders(token),
        ...customHeaders,
        "Content-Type": "application/json",
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });

    if (!response.ok) {
      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return custom({ url, method, payload, headers: customHeaders, query });
        }
      }
      throw new Error(`Request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return { data };
  },
};

async function refreshAccessToken(): Promise<string | null> {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error || !data.session?.access_token) {
      await authProvider.logout({});
      return null;
    }

    localStorage.setItem("access_token", data.session.access_token);
    if (data.session.refresh_token) {
      localStorage.setItem("refresh_token", data.session.refresh_token);
    }
    return data.session.access_token;
  } catch (error) {
    console.error("Refresh token error:", error);
    await authProvider.logout({});
    return null;
  }
}

export default dataProvider;
